import { useSyncExternalStore } from 'react';
import { AUTH_KEY } from '@shared/auth/authentication.hooks';

/**
 * Reactive read side of the Cognito `AUTH_KEY` flag. Fixes "logging in
 * doesn't show the logout button until a full reload": `basic`/`magshop`/
 * `magazine`'s `App.router.tsx` used to compute `onClickLogOut` once, from
 * a plain `localStorage.getItem(AUTH_KEY)` read at module-import time, so
 * a same-tab login (via the `/callback` route) never updated it.
 *
 * Scope: only the Cognito write sites (`services/auth/tokens.ts`'s
 * `saveTokens`/`clearTokens`, and `cognito.ts`'s admin branch) call
 * `notifyAuthStateChange()`. The other auth types (`login`/`jwt`, `token`,
 * `date-token`) don't yet — add a call there too if their routers ever
 * need the same reactivity.
 */
const AUTH_STATE_CHANGE_EVENT = 'canvasflow:auth-state-change';

export function notifyAuthStateChange(): void {
	window.dispatchEvent(new Event(AUTH_STATE_CHANGE_EVENT));
}

export function useIsAuthenticated(): boolean {
	return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

function getSnapshot(): boolean {
	return localStorage.getItem(AUTH_KEY) === 'true';
}

function subscribe(onStoreChange: () => void): () => void {
	// 'storage' only fires in *other* tabs; the custom event covers the tab
	// that actually made the change (e.g. the /callback route on login).
	window.addEventListener('storage', onStoreChange);
	window.addEventListener(AUTH_STATE_CHANGE_EVENT, onStoreChange);
	return () => {
		window.removeEventListener('storage', onStoreChange);
		window.removeEventListener(AUTH_STATE_CHANGE_EVENT, onStoreChange);
	};
}
